import { Box, Divider, SxProps, Table, TableBody, Theme } from '@mui/material'
import _ from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import ApiUrlCompiler from '../../../backend/api_urls/ApiUrlCompiler'
import HttpClient from '../../../backend/HttpClient'
import LocalCache from '../../../backend/LocalCache'
import affirm from '../../../core/Affirm'
import algo from '../../../core/Algo'
import { AttributeFilter, IPagination, IPaginationQueryResponse } from '../../../definitions/Common'
import Helper from '../../../helpers/Helper'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import { snackbar } from '../../services/SnackbarService'
import CollapsibleRow from './CollapsibleRow'
import LoadingRow, { LOADING_ROW_HEIGHT } from './LoadingRow'
import { Cell, Column, ISmartTableDatasource, ISmartTableFilter, ISmartTableFooter, Row, SmartTableToolbar, SortOrder } from './SmartTableDefinitions'
import SmartTableHeader from './SmartTableHeader'
import SmartTableUtilities from './SmartTableUtilities'
import StandardRow from './StandardRow'
import TableFooter from './TableFooter'
import TableToolbar from './TableToolbar'

interface P<T extends object> {
    columns: Column<T>[]
    datasource: ISmartTableDatasource<T>
    sorting?: boolean
    noCollapse?: boolean
    noHeader?: boolean
    sticky?: boolean
    isLoading?: boolean
    toolbar?: SmartTableToolbar<T>
    onClickAddButton?: () => void
    onClickRow?: (indexRow: number, cells: Cell[], row?: Row, item?: T) => void
    footer?: ISmartTableFooter
    onVisibleRowsChange?: (rows: T[]) => void
    sx?: SxProps<Theme>
    style?: React.CSSProperties
    hidePagination?: boolean
}

function SmartTable<T extends object = any>({ columns: originalColumns, datasource, sticky, noCollapse, noHeader, toolbar, onClickRow, isLoading, onClickAddButton, onVisibleRowsChange, sx, style, hidePagination }: P<T>) {
    const [order, setOrder] = useState<SortOrder>('asc')
    const [orderBy, setOrderBy] = useState<string>(null)
    const [columns, setColumns] = useState<Column<T>[]>(originalColumns)
    const [filters, setFilters] = useState<ISmartTableFilter<T>[]>([{ column: columns.find(col => col.id === 'name') || columns[0], operator: 'contains', value: '' }])
    const [search, setSearch] = useState<string>()
    const tableRef = useRef<HTMLTableElement>(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [tokenHistory, setTokenHistory] = useState<string[]>([undefined])
    const [hasNextPage, setHasNextPage] = useState(false)
    const [lastNotifiedRows, setLastNotifiedRows] = useState<T[]>([])
    const [searchParams, setSearchParams] = useState<AttributeFilter>(null)
    const batchSize = 25

    affirm(datasource, 'The data source is missing or undefined.')
    affirm(datasource.rows || datasource.items || datasource.remote, 'The data source must contain rows, items, or a remote configuration.')
    affirm(toolbar ? !datasource.rows : true, 'Invalid configuration: if using "toolbar", then data needs to be passed via "items" or "remote", not via "rows".')

    const loadingRowsNumber = _.max([Math.floor((tableRef?.current?.offsetHeight ?? 0) / LOADING_ROW_HEIGHT), 1])
    const currentToken = tokenHistory[currentPage - 1]

    const { status, response } = useQuery<IPaginationQueryResponse<T>>({
        queryKey: buildQueryKey(datasource?.remote?.url, currentPage, !searchParams?.value ? (currentToken ?? '[FIRSTPAGE]') : searchParams?.value),
        queryFn: async () => {
            if (!datasource.remote) return null
            let body: any = {
                pagination: { batchSize: batchSize,
                    nextToken: currentToken } as IPagination
            }
            if (searchParams?.value) {
                body.searchParams = searchParams as AttributeFilter
            }
            if (datasource?.remote?.parameters)
                body = { ...datasource.remote.parameters, ...body }

            const { headers, url } = ApiUrlCompiler.compile(datasource.remote.url)
            const res = await HttpClient.post<IPaginationQueryResponse<T>>(url, body, headers)

            if (datasource.remote.onSuccess)
                return datasource.remote.onSuccess(res)
            else
                return res
        },
        cache: !datasource?.remote?.noCache && !searchParams?.value
    })

    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchParams({ value: search, attributeName: toolbar?.search })
            setCurrentPage(1)
        }, 1000)

        return () => {
            clearTimeout(handler)
        }
    }, [search])

    useEffect(() => {
        if (response && response.nextToken === 'expiredToken') {
            snackbar.show({ severity: 'info', text: 'Token expired: Reload table' })
            LocalCache.clear(datasource?.remote?.url + '/*')
            setTokenHistory([undefined])
            setCurrentPage(1)
        }

        if (response && response.nextToken !== 'expiredToken') {
            const hasNext = algo.hasVal(response.nextToken)
            setHasNextPage(hasNext)

            if (hasNext) {
                setTokenHistory(prev => {
                    if (prev.length === currentPage) {
                        if (prev[prev.length - 1] !== response.nextToken) {
                            return [...prev, response.nextToken]
                        }
                    } else if (prev.length === currentPage + 1 && prev[currentPage] !== response.nextToken) {
                        const newHistory = [...prev]
                        newHistory[currentPage] = response.nextToken
                        return newHistory
                    }
                    return prev
                })
            }

            if (!hasNext && tokenHistory.length > currentPage) {
                setTokenHistory(prev => prev.slice(0, currentPage))
            }
        }
    }, [response, currentPage])

    const sourceRows = useMemo(() => {
        if (Helper.hasVal(datasource.items) && Array.isArray(datasource.items)) {
            return SmartTableUtilities.extrapolateRows(columns, datasource.items) || []
        } else if (Helper.hasVal(datasource.rows)) {
            return datasource.rows
        } else if (Helper.hasVal(datasource.remote) && response && response.items) {
            return SmartTableUtilities.extrapolateRows(columns, response.items) ?? []
        }
        return []
    }, [datasource, response, columns])

    const rows = useMemo(() => {
        let currentRows = sourceRows

        if (toolbar) {
            if (toolbar.filters && filters && filters.length > 0) {
                currentRows = currentRows.filter(row =>
                    filters.filter(f => Helper.hasVal(f.value) && f.value.length > 0)
                        .every(f => {
                            const cellValue = row.cells[columns.findIndex(c => c.id === f.column.id)]?.value
                            const filterValue = f.value
                            if (!Helper.hasVal(cellValue)) return false

                            const cellValueNum = Number(cellValue)
                            const filterValueNum = Number(filterValue)
                            const bothNumeric = !isNaN(cellValueNum) && !isNaN(filterValueNum)
                            const cellValueStr = cellValue.toString().toLowerCase()
                            const filterValueStr = filterValue.toString().toLowerCase()

                            switch (f.operator) {
                                case 'contains': return cellValueStr.includes(filterValueStr)
                                case 'notContains': return !cellValueStr.includes(filterValueStr)
                                case 'equals': return cellValueStr === filterValueStr
                                case 'notEquals': return cellValueStr !== filterValueStr
                                case 'greaterThan': {
                                    if (bothNumeric) return cellValueNum > filterValueNum
                                    else return cellValueStr > filterValueStr
                                }
                                case 'lessThan': {
                                    if (bothNumeric) return cellValueNum < filterValueNum
                                    else return cellValueStr < filterValueStr
                                }
                                default: return true
                            }
                        })
                )
            }
            if (toolbar.search && toolbar.search.length > 0 && search && search.length > 0 && !datasource.remote) {
                // @ts-ignore
                currentRows = currentRows.filter(row => toolbar.search.some(x => row.cells[columns.findIndex(col => col.id === x)]?.value?.toString().toLowerCase().includes(search?.toLowerCase())))
            }
        }

        const colIndex = columns.findIndex(x => x.id === orderBy) - (noCollapse ? 0 : 1)
        if (colIndex < 0) return currentRows
        else return _.orderBy(currentRows, x => x.cells[colIndex].value, order)
    }, [order, orderBy, columns, filters, search, sourceRows, toolbar, noCollapse])

    const handleSort = (column: Column) => {
        if (orderBy === column.id) {
            if (order === 'asc') setOrder('desc')
            else if (order === 'desc') setOrder('asc')
        } else {
            setOrder('asc')
            setOrderBy(column.id)
        }
    }

    const getItem = (row: Row) => {
        const pkColumnIndex = columns.findIndex(x => x.isPK)
        const pkColumn = columns[pkColumnIndex]

        if (pkColumnIndex === -1 || !Helper.hasVal(pkColumn.id)) {
            console.warn('Primary key not found')
            return undefined
        }

        const pk = row.cells[pkColumnIndex].value

        let item
        if (Helper.hasVal(datasource.items) && Array.isArray(datasource.items)) {
            item = datasource.items.find(x => x[pkColumn.id as string] === pk)
        } else if (Helper.hasVal(datasource.rows)) {
            console.warn('onClickRow handler support only remote datasource')
            if (onClickRow)
                onClickRow(row._index, row.cells, row)
            return
        } else if (Helper.hasVal(datasource.remote)) {
            item = response?.items?.find(x => x[pkColumn.id as string] === pk)
        }

        return item
    }

    const onClick = (_: number, row: Row) => {
        const item = getItem(row)

        if (onClickRow)
            onClickRow(row._index, row.cells, row, item)
    }

    const handlePageChange = (newPage: number) => {
        if (newPage < 1) return
        if (newPage > currentPage && !hasNextPage) return

        setCurrentPage(newPage)
    }

    useEffect(() => {
        if (onVisibleRowsChange) {
            const visibleItems = rows.map(row => getItem(row)).filter(Boolean) as T[]
            if (!_.isEqual(visibleItems, lastNotifiedRows)) {
                setLastNotifiedRows(visibleItems)
                onVisibleRowsChange(visibleItems)
            }
        }
    }, [rows, onVisibleRowsChange, lastNotifiedRows])

    const loading = isLoading || status === 'loading'
    const stalePengind = status === 'stale' || status === 'loading'

    return (
        <>
            {toolbar && (
                <>
                    <TableToolbar toolbar={toolbar} columns={columns} setColumns={setColumns} filters={filters} setFilters={setFilters} search={search} setSearch={setSearch} rows={rows} />
                    <Divider />
                </>
            )}
            <Box
                sx={{
                    'width': '100%',
                    'overflow': 'auto',
                    '& table': {
                        width: '100%',
                        tableLayout: 'fixed'
                    }, ...sx
                }}
                style={style}>
                <Table size='small' style={{ flex: loading ? 1 : undefined }} ref={tableRef}>
                    {!noHeader && (
                        <SmartTableHeader
                            columns={columns}
                            order={order}
                            orderBy={orderBy}
                            sticky={sticky}
                            onClickAddButton={onClickAddButton}
                            onSort={handleSort}
                        />
                    )}
                    <TableBody>
                        {loading
                            ? Array(loadingRowsNumber).fill(0).map((x, i) => {
                                return <LoadingRow key={i} columns={columns} />
                            })
                            : rows.map((row, i) => {
                                const item = getItem(row)

                                if (noCollapse) {
                                    return (
                                        <StandardRow
                                            key={i}
                                            index={i}
                                            item={item}
                                            row={row}
                                            onClickRow={onClickRow && onClick}
                                            columns={columns}
                                        />

                                    )
                                } else {
                                    return (
                                        <CollapsibleRow
                                            key={i}
                                            item={item}
                                            index={i}
                                            row={row}
                                            onClickRow={onClickRow && onClick}
                                            columns={columns}
                                            subContent={row.subContent}
                                        />
                                    )
                                }
                            })}
                    </TableBody>
                </Table>
            </Box>
            <TableFooter
                hidePagination={hidePagination}
                footer={{
                    currentPage: currentPage,
                    hasNextPage: hasNextPage,
                    batchSize: batchSize,
                    onPageChange: handlePageChange
                }}
                isLoading={stalePengind}
                rows={rows}
            />
        </>
    )
}

export default SmartTable
